import React, { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Formik, Form, FieldArray } from 'formik';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import * as Yup from 'yup';

// Components
import PropertyEventList from './PropertyEventList';
import PropertyPhotos from './PropertyPhotos';
import PropertyReactivation from './PropertyReactivation';
import PropertySitePlan from './PropertySitePlan';
import PropertyContacts from '../PropertyContacts';
import TipsWindow from '../../_molecules/TipsWindow';
import PropertyFormFields, { propertyValidationSchema } from '../../_molecules/PropertyFormFields';
import InputList from '@atoms/InputList';
import TextInput from '@atoms/TextInput';
import SubmitButton from '@atoms/SubmitButton';
import { BodyMedium, BodySmall, ButtonNoStyles, ButtonPrimaryLarge, H2, H4, LabelLarge } from '@styled';

// Helpers
import usePropertyDelete from '../../../hooks/properties/usePropertyDelete';
import { useDeleteListingModal } from '../../modals/DeletePropertyModal';
import { mapGraphPropertyToForm } from '@general/transforms/property.transform';
import { transformFormToProperty } from '@general/helpers/property.helpers';
import useEditProperty from '@hooks/useEditProperty';

const tips = [
    {
        title: 'Craft a good first impression',
        message: 'Help your property stand out by adding high-quality photos.',
        cta: {
            name: 'Add Photos',
            link: '#propertyPhotos',
        },
    },
    {
        title: 'Get your property noticed',
        message: 'Associate your property to an event.',
        cta: {
            name: 'Add Event',
            link: '#propertyEvents',
        },
    },
    {
        title: 'Make yourself available',
        message: 'Add property contacts so people know who to contact.',
        cta: {
            name: 'Add Contact',
            link: '#propertyContacts',
        },
    },
];

const SectionContainer = styled.div`
    margin-bottom: 50px;
`;

const PropertyEdit = ({ graphProperty }) => {
    const [property, setProperty] = useState(mapGraphPropertyToForm({ property: graphProperty }));
    const formikRef = useRef();
    const { openModal: openDeleteListingModal } = useDeleteListingModal();
    const history = useHistory();
    const [isSuccessfulUpdate, setIsSuccessfulUpdate] = useState(false);
    const [isErrorUpdate, setIsErrorUpdate] = useState(false);

    // -----------------------------
    // Helpers/Functions
    // -----------------------------

    const { editProperty, isLoading: isLoadingUpdate } = useEditProperty({
        onCompleted: propertyResponse => {
            setProperty(mapGraphPropertyToForm({ property: propertyResponse.updateProperty }));
            setIsSuccessfulUpdate(true);
            setTimeout(() => {
                setIsSuccessfulUpdate(false);
            }, 3000);
        },
        onError: () => {
            setIsErrorUpdate(true);
            setTimeout(() => {
                setIsErrorUpdate(false);
            }, 3000);
        },
    });

    const removeProperty = usePropertyDelete({
        onCompleted: () => {
            console.log('Property deleted');
        },
    });

    const handleSubmit = () => {
        formikRef.current.submitForm().then(() => {
            if (Object.keys(formikRef.current.errors).length === 0) {
                const newProperty = {
                    id: property.id,
                    ...transformFormToProperty(formikRef.current.values),
                };

                editProperty({ variables: newProperty });
            }
        });
    };

    const cancelChanges = () => {
        history.goBack('/my-icsc/properties/listed');
    };

    // -----------------------------
    // Render
    // -----------------------------

    return (
        <div>
            <TipsWindow title="Improve this listing" tips={tips} />

            <div className="d-flex justify-content-between flex-column flex-md-row">
                <H2>{property.title}</H2>
            </div>

            <BodySmall>
                Last updated {property.updatedAt && format(new Date(property.updatedAt), 'MMMM d, yyyy')}
            </BodySmall>

            <PropertyReactivation propertyId={property.id} expiresAt={property.expiresAt} />

            <H4 className="font-weight-bold my-4">Basic Details</H4>

            <div className="my-4">
                <Formik
                    innerRef={formikRef}
                    enableReinitialize
                    initialValues={property}
                    validationSchema={Yup.object(propertyValidationSchema)}
                    onSubmit={() => {}}
                >
                    {props => (
                        <Form noValidate style={{ display: 'flex', flexDirection: 'column' }}>
                            <PropertyFormFields showSubmitButtons={false} />

                            <div>
                                <H4 className="font-weight-bold my-2">Affiliated Businesses</H4>
                                <LabelLarge className="form-control-label">Business Name</LabelLarge>
                                <FieldArray
                                    name="businesses"
                                    render={arrayHelpers => (
                                        <InputList
                                            name="businesses"
                                            list={props.values.businesses}
                                            buttonText="Add a Business"
                                            placeholder="Business Name"
                                            field={TextInput}
                                            arrayHelpers={arrayHelpers}
                                        />
                                    )}
                                />
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>

            <SectionContainer>
                <H4 className="font-weight-bold my-2">Additional Details</H4>
                <BodyMedium>
                    Make your listing more appealing by uploading photos and/or a site plan. Associate your listing with
                    other ICSC details like events and contacts. Add affiliated businesses so people can gain a sense of
                    the surrounding retail community.
                </BodyMedium>
            </SectionContainer>

            <SectionContainer id="propertyEvents">
                <PropertyEventList
                    propertyId={property.id}
                    events={property.events.filter(e => !!e).map(e => e.code)}
                />
            </SectionContainer>

            <SectionContainer id="propertyPhotos">
                <PropertyPhotos propertyId={property.id} photos={property.photos} />
            </SectionContainer>

            <SectionContainer id="propertyContacts">
                <PropertyContacts propertyId={property.id} contacts={property.contacts} />
            </SectionContainer>

            <div className="mb-2.25rem">
                <PropertySitePlan propertyId={property.id} sitePlan={property.sitePlan} />
            </div>

            <SectionContainer className="d-inline-flex flex-wrap align-items-center">
                <div className="mx-auto mr-md-4 ml-md-0 -mt-2rem">
                    <SubmitButton
                        type="submit"
                        buttonClasses="editproperty mt-0"
                        onClick={handleSubmit}
                        isSubmitting={isLoadingUpdate}
                        isSuccessful={isSuccessfulUpdate}
                        isError={isErrorUpdate}
                        submittingText="Saving Property"
                        errorText="Failed to Save Property"
                        successText="Property Saved"
                    >
                        Save
                    </SubmitButton>
                </div>

                <div className="mx-auto mr-md-4 ml-md-0">
                    <ButtonPrimaryLarge className="mb-1" onClick={cancelChanges}>
                        Cancel Changes
                    </ButtonPrimaryLarge>
                </div>

                <div className="mx-auto mx-md-0">
                    <ButtonNoStyles
                        type="reset"
                        className="text-primary mb-1"
                        onClick={() =>
                            openDeleteListingModal(property.title, async () => {
                                history.goBack();
                                removeProperty(property.id);
                            })
                        }
                    >
                        Delete Listing
                    </ButtonNoStyles>
                </div>
            </SectionContainer>
        </div>
    );
};

PropertyEdit.propTypes = {
    graphProperty: PropTypes.shape({
        id: PropTypes.string.isRequired,
    }).isRequired,
};

export default PropertyEdit;
